'use client';

import { motion } from 'framer-motion';
import {
  getTileBackgroundClasses,
  getTileBorderClasses,
  getTileTextClasses,
} from '../helpers';
import type { Tile } from './WordleGrid';

export interface TileCellProps {
  tile: Tile;
  isActive: boolean;
  revealDelay?: number;
  dateKey?: string;
  cellIndex?: number;
}

export function TileCell({
  tile,
  isActive,
  revealDelay = 0,
  dateKey,
  cellIndex = 0,
}: TileCellProps) {
  const { letter, state, locked } = tile;

  const isRevealed = state !== 'default';
  const isAutoRevealed = locked && state === 'default';

  const backgroundClasses = getTileBackgroundClasses(state);
  const textClasses = getTileTextClasses(state);
  const borderClasses = (() => {
    if (isActive) return 'border-[var(--semantic-border-focus)]';

    // ✅ lettre révélée → plus de bordure
    if (state !== 'default') return 'border-transparent';

    // ✅ lettre tapée
    if (letter) return 'border-[var(--semantic-border-filled)]';

    // ✅ case vide → bordure visible
    return getTileBorderClasses('default');
  })();

  // Animation d'entrée lors du changement de date (même style que DatePickerModal)
  const entryDelay = dateKey !== undefined ? (cellIndex % 5) * 0.02 : 0;

  return (
    <motion.div
      className="relative w-[var(--tile-size,4rem)] h-[var(--tile-size,4rem)] preserve-3d"
      key={dateKey ? `date-${dateKey}` : undefined} // Change la clé pour déclencher l'animation lors du changement de date
      initial={dateKey !== undefined ? { opacity: 0, scale: 0.8 } : false}
      animate={{
        opacity: 1,
        scale: letter && !isRevealed ? 1.06 : 1,
        rotateX: isRevealed ? 180 : 0,
      }}
      transition={{
        opacity: {
          duration: 0.15,
          delay: entryDelay,
        },
        scale: {
          type: 'spring',
          stiffness: 300,
          damping: 18,
        },
        rotateX: {
          duration: 0.45,
          ease: 'easeInOut',
          delay: revealDelay,
        },
      }}
    >
      {/* Face avant */}
      <div
        style={{ fontSize: 'calc(var(--tile-size, 4rem) * 0.5)' }}
        className={`
          absolute inset-0
          flex items-center justify-center
          border-3 rounded-md
          backface-hidden
          font-bold uppercase
          ${borderClasses}
          ${textClasses}
          ${
            isAutoRevealed
              ? 'border-1 border-dashed border-[var(--tile-auto-border)] bg-[var(--tile-auto-bg)]'
              : ''
          }
        `}
      >
        {letter}
      </div>
  
      <div
        style={{ fontSize: 'calc(var(--tile-size, 4rem) * 0.5)' }}
        className={`
          absolute inset-0
          flex items-center justify-center
          rotate-x-180 backface-hidden
          font-bold uppercase
          ${backgroundClasses}
          ${textClasses}
        `}
      >
        {letter}
      </div>
    </motion.div>
  );
}
